/**
 * Access Log Tab Component
 *
 * Customer view of admin access logs (transparency feature).
 *
 * DESIGN:
 * - Table of admin access entries
 * - Columns: Date, Admin Name, Action, Details
 * - Filter by date range
 * - Export to CSV
 *
 * FEATURES:
 * - Standard table with cyan-600 header
 * - Shows admin NAME not UUID (JOIN)
 * - RLS auto-filters to org's logs only
 *
 * BUG FIX:
 * - Bug #17: Join with users table to show admin name
 *
 * INTEGRATION:
 * - Add tab to AccountPage.js
 * - Fetch from admin_access_log via Supabase
 */

import React, { useState, useEffect } from 'react';
import {
  Shield,
  Calendar,
  Download,
  Search,
  Eye,
  Clock,
  User,
  AlertCircle,
  Loader2
} from 'lucide-react';
// TODO: Migrate to Azure access log API (GET /api/admin/access-logs)
// import { getSupabaseClient } from '../../../lib/supabase';
import { devLog } from '../../../utils/devLog';

const AccessLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });

  useEffect(() => {
    loadAccessLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, dateRange]);

  const loadAccessLogs = async () => {
    // TODO: Migrate to Azure API - GET /api/admin/access-logs
    // For now, show graceful degradation message
    setIsLoading(false);
    setError('Access log temporarily unavailable during Azure migration. Full access log transparency will be restored in Phase 9-10.');
    setLogs([]);
    setFilteredLogs([]);

    /* ORIGINAL SUPABASE CODE - TO BE REPLACED WITH AZURE API
    try {
      setIsLoading(true);
      setError(null);

      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase client not available');
      }

      // Bug #17 FIX: JOIN with users table to show admin name
      // RLS will auto-filter to current org's logs only
      const { data, error: fetchError } = await supabase
        .from('admin_access_log')
        .select(`
          id,
          session_id,
          action_type,
          action_details,
          created_at,
          admin_user_id,
          users!admin_user_id (
            name,
            email
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (fetchError) {
        throw fetchError;
      }

      // Transform data to include admin name
      const logsWithNames = (data || []).map(log => ({
        ...log,
        adminName: log.users?.name || log.users?.email || 'Unknown Admin',
        adminEmail: log.users?.email || 'N/A'
      }));

      setLogs(logsWithNames);
      setFilteredLogs(logsWithNames);

      devLog('[ACCESS LOG] Loaded logs:', logsWithNames.length);

    } catch (err) {
      console.error('[ACCESS LOG] Failed to load:', err);
      setError(err.message || 'Failed to load access logs');
    } finally {
      setIsLoading(false);
    }
    */
  };

  const filterLogs = () => {
    let filtered = [...logs];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log =>
        log.adminName.toLowerCase().includes(term) ||
        log.action_type.toLowerCase().includes(term) ||
        (log.action_details?.reason || '').toLowerCase().includes(term)
      );
    }

    // Date range filter
    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(log =>
        new Date(log.created_at) >= startDate
      );
    }

    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(log =>
        new Date(log.created_at) <= endDate
      );
    }

    setFilteredLogs(filtered);
  };

  const exportToCSV = () => {
    if (filteredLogs.length === 0) {
      alert('No logs to export');
      return;
    }

    const headers = ['Date/Time', 'Admin', 'Action', 'Details'];
    const rows = filteredLogs.map(log => [
      new Date(log.created_at).toLocaleString(),
      log.adminName,
      formatActionType(log.action_type),
      log.action_details?.reason || JSON.stringify(log.action_details || {})
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatActionType = (type) => {
    const formatted = type.replace(/_/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'impersonation_start':
      case 'impersonation_end':
        return Shield;
      case 'data_view':
        return Eye;
      default:
        return AlertCircle;
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case 'impersonation_start':
        return 'text-yellow-600 bg-yellow-50';
      case 'impersonation_end':
        return 'text-green-600 bg-green-50';
      case 'data_view':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-slate-600 bg-slate-50';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-cyan-900 mb-1">
              Admin Access Transparency
            </h3>
            <p className="text-cyan-800 text-sm">
              This log shows all times when a FleetSkipper administrator accessed your organization's data
              for support purposes. All sessions are time-limited and require a documented reason.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        {/* Search */}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by admin name, action, or reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-400" />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
          />
          <span className="text-slate-500">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Export Button */}
        <button
          onClick={exportToCSV}
          disabled={filteredLogs.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {searchTerm || dateRange.start || dateRange.end
              ? 'No logs match your filters'
              : 'No admin access logs found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date/Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredLogs.map((log, index) => {
                  const ActionIcon = getActionIcon(log.action_type);
                  const actionColor = getActionColor(log.action_type);

                  return (
                    <tr key={log.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      {/* Date/Time */}
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <div>
                            <div>{new Date(log.created_at).toLocaleDateString()}</div>
                            <div className="text-xs text-slate-500">
                              {new Date(log.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Admin */}
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <div>
                            <div className="font-medium">{log.adminName}</div>
                            <div className="text-xs text-slate-500">{log.adminEmail}</div>
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${actionColor} font-medium`}>
                          <ActionIcon className="h-3.5 w-3.5" />
                          {formatActionType(log.action_type)}
                        </span>
                      </td>

                      {/* Details */}
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {log.action_details?.reason || (
                          <span className="text-slate-400 italic">No details provided</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {filteredLogs.length > 0 && (
        <div className="text-sm text-slate-500 text-center">
          Showing {filteredLogs.length} of {logs.length} total access log{logs.length === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
};

export default AccessLogTab;
